import { api } from './client'

export interface Organization {
  id: number
  name: string
  slug: string
  description: string | null
  created_by: number | null
  responsible_user_id: number | null
  responsible_user_name: string | null
  created_at: string
  updated_at: string
  member_count?: number
  members?: OrganizationMember[]
  logo_url?: string | null
  /** Falso = a organização não faz controle de mudança: nenhuma ação dela passa
   *  por GMUD, nem as críticas. Ausente em resposta antiga → tratar como true. */
  requires_change_control?: boolean
  /** Spec 2026-10-08 D9: ambientes em que a GMUD é exigida nesta organização.
   *  Ausente (resposta antiga) → tratar como ['prd'], a semente da migration. */
  change_control_environments?: string[]
  /**
   * Código curto que entra no número de protocolo dos chamados desta
   * organização, junto do ambiente: CHM-<code>-<AMBIENTE>-<ano>-<seq>.
   * Alterar não renumera chamados já abertos — o protocolo é imutável.
   */
  ticket_code?: string | null
}

export interface LogoPresignResponse {
  supported: boolean
  upload_url?: string
  s3_key?: string
}

export interface GestorOption {
  id: number
  full_name: string
  email: string
}

export interface OrganizationMember {
  id: number
  organization_id: number
  tenant_id: number
  tenant_name: string
  ou_id: string
  environment: string | null
  added_at: string
}

export interface JiraOrgConfig {
  id: number
  organization_id: number
  jira_base_url: string
  jira_project_key: string
  jira_board_id: number | null
  sync_enabled: boolean
  poll_interval_seconds: number
  sync_scope: Record<string, unknown>
  created_at: string
  updated_at: string
}

export interface JiraLinkResult {
  config: JiraOrgConfig
  test_result: { success: boolean; project_name?: string; total_issues?: number; error?: string }
  parsed: { base_url: string; project_key: string; board_id: number }
}

/** Conta PRD do projeto pedida junto da organizacao (A2): o job ou o erro. */
export interface ContaPrdResultado {
  job_id?: number
  erro?: string
}

export interface DisponibilidadeContaPrd {
  disponivel: boolean
  motivo: string | null
}

/** Resposta de `POST /organizations/{id}/tenant-de-atendimento` (consoleai#26145). */
export interface TenantDeAtendimento {
  organization_id: number
  tenant_id: number
  criado: boolean
  /** O tenant de atendimento estava desativado e voltou ativo. */
  reativado: boolean
  usuarios_reconciliados: number[]
}

export const organizationsApi = {
  list: () =>
    api.get<{ items: Organization[] }>('/v1/organizations').then(r => r.data.items),

  get: (orgId: number) =>
    api.get<Organization & { members: OrganizationMember[] }>(`/v1/organizations/${orgId}`).then(r => r.data),

  create: (data: {
    name: string
    description?: string
    responsible_user_id: number
    project_slug?: string
    criar_conta_prd?: boolean
  }) =>
    api.post<Organization & { conta_prd?: ContaPrdResultado }>('/v1/organizations', data)
      .then(r => r.data),

  contaPrd: (orgId: number, project_slug: string) =>
    api.post<{ job_id: number }>(`/v1/organizations/${orgId}/conta-prd`, { project_slug })
      .then(r => r.data),

  disponibilidadeContaPrd: () =>
    api.get<DisponibilidadeContaPrd>('/v1/organizations/conta-prd/disponibilidade')
      .then(r => r.data),

  /** Organização sem AWS: cria (ou reaproveita) o tenant em que ela abre chamado. Só admin. */
  tenantDeAtendimento: (orgId: number) =>
    api.post<TenantDeAtendimento>(`/v1/organizations/${orgId}/tenant-de-atendimento`)
      .then(r => r.data),

  update: (orgId: number, data: { name?: string; description?: string; responsible_user_id?: number; requires_change_control?: boolean; ticket_code?: string; change_control_environments?: string[] }) =>
    api.put<Organization>(`/v1/organizations/${orgId}`, data).then(r => r.data),

  delete: (orgId: number) =>
    api.delete(`/v1/organizations/${orgId}`).then(r => r.data),

  addMember: (orgId: number, tenantId: number, environment?: string) =>
    api.post<OrganizationMember>(`/v1/organizations/${orgId}/members`, {
      tenant_id: tenantId, environment,
    }).then(r => r.data),

  // `confirmarSoc`: o servidor devolve 409 `soc_team_ativo` quando o tenant tem
  // time de SOC ativo, ate a remocao ser confirmada (TASK-11548).
  removeMember: (orgId: number, tenantId: number, confirmarSoc = false) =>
    api.delete(`/v1/organizations/${orgId}/members/${tenantId}`,
      confirmarSoc ? { params: { confirmar_soc: true } } : undefined).then(r => r.data),

  // --- Logo da organização (presign → PUT direto no S3 → confirm) ---
  presignLogo: (
    orgId: number,
    body: { filename: string; mime_type: string; size_bytes: number },
  ) =>
    api.post<LogoPresignResponse>(`/v1/organizations/${orgId}/logo/presign`, body)
      .then(r => r.data),

  // PUT direto no S3 com Content-Type EXATO — NÃO usar a instância `api`
  // (o presign travou ContentType/ContentLength; headers extras invalidam a assinatura).
  uploadLogoToS3: async (uploadUrl: string, file: File): Promise<void> => {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      body: file,
    })
    if (!res.ok) throw new Error(`s3_put_failed_${res.status}`)
  },

  confirmLogo: (orgId: number, s3_key: string) =>
    api.post<{ logo_url: string | null }>(`/v1/organizations/${orgId}/logo/confirm`, { s3_key })
      .then(r => r.data),

  deleteLogo: (orgId: number) =>
    api.delete<{ ok: boolean }>(`/v1/organizations/${orgId}/logo`).then(r => r.data),

  getJiraConfig: (orgId: number) =>
    api.get<JiraOrgConfig>(`/v1/organizations/${orgId}/jira/config`).then(r => r.data),

  linkJira: (orgId: number, jiraUrl: string) =>
    api.post<JiraLinkResult>(`/v1/organizations/${orgId}/jira/link`, { jira_url: jiraUrl }).then(r => r.data),

  toggleJiraSync: (orgId: number, enabled: boolean) =>
    api.put<JiraOrgConfig>(`/v1/organizations/${orgId}/jira/toggle`, { enabled }).then(r => r.data),

  listGestores: () =>
    api.get<{ items: GestorOption[] }>('/v1/organizations/gestores').then(r => r.data.items),

  listOrgUsers: (orgId: number) =>
    api.get<{ items: OrgUser[] }>(`/v1/organizations/${orgId}/users`).then(r => r.data.items),

  addOrgUser: (orgId: number, userId: number, role: string = 'member', templateId?: number | null) => {
    const body: { user_id: number; role: string; template_id?: number } = { user_id: userId, role }
    if (templateId != null) body.template_id = templateId
    return api.post(`/v1/organizations/${orgId}/users`, body).then(r => r.data)
  },

  // Catálogo de templates disponíveis da organização (MIGRATION_134).
  // GET → templates do catálogo; PUT → sync-total (gate: admin OU gestor da org).
  getOrganizationTemplates: (orgId: number) =>
    api.get<{ templates: { id: number; name: string }[] }>(`/v1/organizations/${orgId}/templates`)
      .then(r => r.data.templates),

  setOrganizationTemplates: (orgId: number, templateIds: number[]) =>
    api.put(`/v1/organizations/${orgId}/templates`, { template_ids: templateIds }).then(r => r.data),

  removeOrgUser: (orgId: number, userId: number) =>
    api.delete(`/v1/organizations/${orgId}/users/${userId}`).then(r => r.data),

  // PATCH /v1/users/{user_id}/organization (atômico — Spec MR !538)
  // Move user entre orgs sincronizando organization_users + opcionalmente
  // applied_template_id + user_tenants + cache de roles.
  moveUserOrganization: (
    userId: number,
    body: {
      new_organization_id: number
      new_template_id?: number | null
      role?: string
    },
  ) =>
    api.patch<{
      status: string
      user_id: number
      organization_id: number
      organization_name: string
      template_id: number | null
      tenant_ids: number[]
    }>(`/v1/users/${userId}/organization`, body).then(r => r.data),

  // ── Solicitações de vínculo (Fase 2 — spec 2026-07-24 §3.1) ──────────────

  /** Lista id+nome de todas as organizações (bearer) — usado no Select de
   *  destino de transferência / entrada. */
  listLite: () =>
    api.get<{ items: OrgLite[] }>('/v1/organizations/lite').then(r => r.data.items),

  /** Pool livre p/ o gestor adicionar direto (usuários sem org). */
  listAssignableUsers: (orgId: number) =>
    api.get<{ items: AssignableUser[] } | AssignableUser[]>(`/v1/organizations/${orgId}/assignable-users`)
      .then(r => (Array.isArray(r.data) ? r.data : r.data.items)),

  /** Gestor de origem solicita a transferência de um member para outra org.
   *  O colaborador perde acesso imediatamente; cria pendência. */
  transferOrgUser: (fromOrgId: number, userId: number, body: { to_org_id: number; note?: string }) =>
    api.post(`/v1/organizations/${fromOrgId}/users/${userId}/transfer`, body).then(r => r.data),

  /** Férias (§3.3): suspende o acesso do colaborador até `until`
   *  (`YYYY-MM-DD`). O retorno é automático — não precisa desmarcar. */
  setOrgUserLeave: (orgId: number, userId: number, until: string, note?: string) =>
    api.post(`/v1/organizations/${orgId}/users/${userId}/leave`, { until, note })
      .then(r => r.data),

  /** Encerra as férias antes da data (devolve o acesso na hora). */
  clearOrgUserLeave: (orgId: number, userId: number) =>
    api.delete(`/v1/organizations/${orgId}/users/${userId}/leave`).then(r => r.data),

  /** Fila de solicitações da org (incoming = fila de aprovação;
   *  outgoing = transferências que esta org enviou).
   *  status='all' → omite o filtro (o backend devolve todos os status). */
  listMembershipRequests: (orgId: number, direction: RequestDirection = 'incoming', status: string = 'pending') => {
    const params: Record<string, string> = { direction }
    if (status && status !== 'all') params.status = status
    return api.get<{ items: MembershipRequest[] } | MembershipRequest[]>(
      `/v1/organizations/${orgId}/membership-requests`,
      { params },
    ).then(r => (Array.isArray(r.data) ? r.data : r.data.items))
  },

  approveMembershipRequest: (orgId: number, reqId: number, templateId?: number | null) => {
    const body: { template_id?: number } = {}
    if (templateId != null) body.template_id = templateId
    return api.post(`/v1/organizations/${orgId}/membership-requests/${reqId}/approve`, body).then(r => r.data)
  },

  rejectMembershipRequest: (orgId: number, reqId: number, decisionNote?: string) =>
    api.post(`/v1/organizations/${orgId}/membership-requests/${reqId}/reject`, { decision_note: decisionNote })
      .then(r => r.data),

  cancelMembershipRequest: (reqId: number, decisionNote?: string) =>
    api.post(`/v1/organizations/membership-requests/${reqId}/cancel`, { decision_note: decisionNote })
      .then(r => r.data),

  // ── Colaborador sem organização (spec 2026-07-24 §3.1 — fluxo join) ──────

  myOrganizationStatus: () =>
    api.get<OrgStatusResponse>('/v1/me/organization-status').then(r => r.data),

  requestJoinOrganization: (body: { to_org_id: number; note?: string }) =>
    api.post('/v1/me/organization-requests', body).then(r => r.data),
}

export type RequestDirection = 'incoming' | 'outgoing'

export interface OrgLite {
  id: number
  name: string
}

export interface AssignableUser {
  id: number
  email: string
  full_name: string
}

export interface MembershipRequest {
  id: number
  user_id: number
  user_full_name: string
  user_email: string
  to_org_id: number
  to_org_name: string
  from_org_id: number | null
  from_org_name: string | null
  kind: 'transfer' | 'join'
  status: 'pending' | 'approved' | 'rejected' | 'cancelled'
  note: string | null
  decision_note: string | null
  created_at: string
}

export interface OrgStatusResponse {
  has_org: boolean
  /** Decidido no servidor: false para quem vive fora das Organizações
   *  (role `auditor_global`). Ausente = API anterior → trata como true. */
  org_required?: boolean
  pending_request: MembershipRequest | null
  organizations: OrgLite[]
}

export interface OrgUser {
  id: number
  user_id: number
  email: string
  full_name: string
  role: string
  added_at: string
  /** Perfil POR ORG (organization_users.template_id) — per-org-template §4/§5.
   *  null quando a associação não tem override per-org (fallback global). */
  template_id: number | null
  template_name: string | null
  /** Férias (§3.3): ISO-8601 até quando o acesso está suspenso; null = sem
   *  férias. Opcional para tolerar respostas antigas em cache do navegador. */
  on_leave_until?: string | null
}
